import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../../UserContext';
import { hasPaidPlan } from '../../../../utils/subscriptionStatus';
import './Blog.css';
import { 
    Newspaper, 
    Eye, 
    Heart, 
    Calendar, 
    User, 
    ChevronRight,
    Search,
    Star,
    Lock,
    Crown
} from 'lucide-react';
import { blogService } from '../../../../services';
import { appConfig } from '../../../config/appConfig';
import PremiumUnlockCard from '../../../components/subscription/PremiumUnlockCard';

const Blog = () => {
    const navigate = useNavigate();
    const { user } = useUser();
    const [posts, setPosts] = useState([]);
    const [featuredPosts, setFeaturedPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [hasPremium, setHasPremium] = useState(false);

    const fetchPosts = useCallback(async () => {
        setIsLoading(true);
        try {
            let data;
            try {
                data = await blogService.getMemberPosts(currentPage, 9, selectedCategory !== 'All' ? selectedCategory : '');
            } catch {
                data = await blogService.getPublicPosts(currentPage, 9, selectedCategory !== 'All' ? selectedCategory : '');
            }
            setPosts(data.posts || []);
            setTotalPages(data.totalPages || 1);
            setHasPremium(Boolean(data.hasPremium));
            setError(null);
        } catch (err) {
            console.error('Error fetching posts:', err);
            setError('Failed to load posts');
        } finally {
            setIsLoading(false);
        }
    }, [selectedCategory, currentPage]);

    const fetchFeaturedPosts = useCallback(async () => {
        try {
            const data = await blogService.getFeaturedPosts(3);
            setFeaturedPosts(data || []);
        } catch (err) {
            console.error('Error fetching featured posts:', err);
        }
    }, []);

    const fetchCategories = useCallback(async () => {
        try {
            const data = await blogService.getCategories();
            setCategories(data || []);
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    }, []);

    useEffect(() => {
        fetchPosts();
        fetchFeaturedPosts();
        fetchCategories();
    }, [fetchPosts, fetchFeaturedPosts, fetchCategories]);

    const handlePostClick = (slug) => {
        navigate(`/dashboard/blog/${slug}`);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const getVisibilityBadge = (visibility) => {
        switch (visibility) {
            case 'Premium':
                return <span className="blog-badge blog-badge-premium"><Crown size={12} /> Premium</span>;
            case 'MembersOnly':
                return <span className="blog-badge blog-badge-members"><Lock size={12} /> Members</span>;
            default:
                return null;
        }
    };

    const filteredPosts = posts.filter(post => 
        searchQuery === '' || 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const hasPremiumAccess = (() => {
        return hasPremium || hasPaidPlan(user?.subscription);
    })();

    return (
        <div className="blog-page">
            <div className="blog-header">
                <div className="blog-header-content">
                    <div className="blog-header-icon">
                        <Newspaper size={28} />
                    </div>
                    <div>
                        <h1>News & Blog</h1>
                        <p>{appConfig.messaging.blogTagline}</p>
                    </div>
                </div>
            </div>

            {/* Featured Posts Section */}
            {featuredPosts.length > 0 && (
                <div className="blog-featured-section">
                    <h2 className="blog-section-title">
                        <Star size={20} className="blog-section-icon" />
                        Featured Stories
                    </h2>
                    <div className="blog-featured-grid">
                        {featuredPosts.map((post, index) => (
                            <div 
                                key={post.postId} 
                                className={`blog-featured-card ${index === 0 ? 'blog-featured-main' : ''}`}
                                onClick={() => handlePostClick(post.slug)}
                            >
                                <div className="blog-featured-image">
                                    {post.featuredImageUrl ? (
                                        <img src={post.featuredImageUrl} alt={post.title} />
                                    ) : (
                                        <div className="blog-featured-placeholder">
                                            <Newspaper size={48} />
                                        </div>
                                    )}
                                    <div className="blog-featured-overlay">
                                        <span className="blog-featured-category">{post.category || 'News'}</span>
                                    </div>
                                </div>
                                <div className="blog-featured-content">
                                    <h3>{post.title}</h3>
                                    <p>{post.excerpt}</p>
                                    <div className="blog-featured-meta">
                                        <span><Calendar size={14} /> {formatDate(post.publishedAt)}</span>
                                        <span><Eye size={14} /> {post.viewCount}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Filters Section */}
            <div className="blog-filters">
                <div className="blog-search">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search articles..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="blog-categories">
                    <button
                        className={`blog-category-btn ${selectedCategory === 'All' ? 'active' : ''}`}
                        onClick={() => { setSelectedCategory('All'); setCurrentPage(1); }}
                    >
                        All
                    </button>
                    {categories.map(cat => (
                        <button
                            key={cat.category}
                            className={`blog-category-btn ${selectedCategory === cat.category ? 'active' : ''}`}
                            onClick={() => { setSelectedCategory(cat.category); setCurrentPage(1); }}
                        >
                            {cat.category} ({cat.count})
                        </button>
                    ))}
                </div>
            </div>

            {/* Posts Grid */}
            {isLoading ? (
                <div className="blog-loading">
                    <div className="blog-loading-spinner"></div>
                    <p>Loading articles...</p>
                </div>
            ) : error ? (
                <div className="blog-error">
                    <p>{error}</p>
                </div>
            ) : filteredPosts.length === 0 ? (
                <div className="blog-empty">
                    <Newspaper size={64} />
                    <h3>No Articles Yet</h3>
                    <p>Check back soon for the latest news and stories</p>
                </div>
            ) : (
                <>
                    <div className="blog-grid">
                        {filteredPosts.map(post => (
                            <div 
                                key={post.postId} 
                                className="blog-card"
                                onClick={() => handlePostClick(post.slug)}
                            >
                                <div className="blog-card-image">
                                    {post.featuredImageUrl ? (
                                        <img src={post.featuredImageUrl} alt={post.title} />
                                    ) : (
                                        <div className="blog-card-placeholder">
                                            <Newspaper size={32} />
                                        </div>
                                    )}
                                    {getVisibilityBadge(post.visibility)}
                                    {post.isFeatured && (
                                        <span className="blog-badge blog-badge-featured">
                                            <Star size={12} /> Featured
                                        </span>
                                    )}
                                </div>
                                <div className="blog-card-content">
                                    <div className="blog-card-category">{post.category || 'News'}</div>
                                    <h3 className="blog-card-title">{post.title}</h3>
                                    <p className="blog-card-excerpt">{post.excerpt}</p>
                                    <div className="blog-card-footer">
                                        <div className="blog-card-author">
                                            {post.authorProfileImage ? (
                                                <img src={post.authorProfileImage} alt={post.authorName} />
                                            ) : (
                                                <div className="blog-card-author-placeholder">
                                                    <User size={14} />
                                                </div>
                                            )}
                                            <span>{post.authorDisplayName || post.authorName}</span>
                                        </div>
                                        <div className="blog-card-stats">
                                            <span><Eye size={14} /> {post.viewCount}</span>
                                            <span><Heart size={14} /> {post.likeCount}</span>
                                        </div>
                                    </div>
                                    <div className="blog-card-date">
                                        <Calendar size={12} /> {formatDate(post.publishedAt)}
                                    </div>
                                </div>
                                <div className="blog-card-arrow">
                                    <ChevronRight size={20} />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="blog-pagination">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                            >
                                Previous
                            </button>
                            <span>Page {currentPage} of {totalPages}</span>
                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Premium Upsell */}
            {!hasPremiumAccess && (
                <div className="blog-premium-banner">
                    <PremiumUnlockCard
                        compact
                        title="Premium Articles, Reviews, and Tips"
                        description={`Free members can still browse public posts. Upgrade when you want premium-only reading and deeper member benefits for the ${appConfig.terminology.communityPlural} community.`}
                        highlights={[
                            'Free keeps marketplace, venue discovery, blogs, and news open.',
                            'Premium unlocks premium-only blog posts and advanced scoring tools.',
                            'Premium adds deeper analytics and extra member perks.'
                        ]}
                        ctaLabel="Unlock Premium Access"
                    />
                </div>
            )}
        </div>
    );
};

export default Blog;
